import { createAsyncThunk } from "@reduxjs/toolkit";
import * as clientsAPI from "../../api/clients";
import { unwrapDashboardResponse } from "../../api/dashboardResponse";
import {
  createDashboardMutationThunk,
  createDashboardQueryThunk,
  createEntityListSlice,
  createEntityListStateUpdater,
  rejectDashboardRequest,
} from "../utils/createEntityListSlice";

const updateClientListState = createEntityListStateUpdater("clientList");

const getClientsData = createDashboardQueryThunk(
  "clients/getClientsData",
  clientsAPI.getClient,
  "Failed to load clients"
);

const searchClientData = createDashboardQueryThunk(
  "clients/searchClientData",
  clientsAPI.searchClient,
  "Search failed"
);

const addClientData = createDashboardMutationThunk(
  "clients/addClientData",
  clientsAPI.addClient,
  {
    fallbackMessage: "Add failed",
    getSuccessMessage: "Clients Added Successfully",
  }
);

const updateClientData = createDashboardMutationThunk(
  "clients/updateClientData",
  clientsAPI.updateClient,
  {
    fallbackMessage: "Update failed",
    getSuccessMessage: (params) =>
      params?.status === "Active"
        ? "Client Updated Successfully"
        : "Client Deleted Successfully",
  }
);

const getClientIdData = createDashboardQueryThunk(
  "clients/getClientIdData",
  clientsAPI.getClientId,
  "Failed to get client"
);

const addClientByClient = createDashboardMutationThunk(
  "clients/addClientByClientUser",
  clientsAPI.addClientByClientUser,
  {
    fallbackMessage: "Add failed",
    getSuccessMessage: "Clients Added Successfully",
  }
);

const getClientDataByClient = createDashboardQueryThunk(
  "clients/getClientDataByClient",
  clientsAPI.getClientsByClientUser,
  "Failed to load clients"
);

const getClientSearch = createDashboardQueryThunk(
  "clients/getClientSearch",
  clientsAPI.getClientSearch,
  "Search failed"
);

const rolesForClient = createAsyncThunk(
  "clients/rolesForClient",
  async (params, { rejectWithValue }) => {
    try {
      const res = await clientsAPI.getClientsByClientUser(params);
      return unwrapDashboardResponse(res, "Failed to load roles");
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to load roles"
      );
    }
  }
);

const clientsSlice = createEntityListSlice({
  name: "clients",
  extraState: {
    clientId: null,
  },
  asyncCases: [
    { thunk: getClientsData },
    {
      thunk: getClientDataByClient,
      options: {
        onRejected: (state) => {
          state.data = [];
        },
      },
    },
    { thunk: searchClientData },
    {
      thunk: getClientSearch,
      options: {
        onRejected: (state) => {
          state.data = [];
        },
      },
    },
    { thunk: addClientData },
    {
      thunk: addClientByClient,
      options: {
        onRejected: (state) => {
          state.data = [];
        },
      },
    },
    {
      thunk: updateClientData,
      options: {
        onFulfilled: updateClientListState,
      },
    },
    {
      thunk: getClientIdData,
      options: {
        dataKey: "clientId",
      },
    },
    {
      thunk: rolesForClient,
      options: {
        onFulfilled: (state, action) => {
          state.clientId = action.payload?.clientList;
        },
        onRejected: (state) => {
          state.clientId = [];
        },
      },
    },
  ],
});

const { resetState, setPageNumber, setRowsPerPage } = clientsSlice.actions;

export default clientsSlice.reducer;

export {
  getClientsData,
  setPageNumber,
  setRowsPerPage,
  resetState,
  searchClientData,
  addClientData,
  updateClientData,
  getClientIdData,
  addClientByClient,
  getClientDataByClient,
  getClientSearch,
  rolesForClient,
};
